import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { testPrepAPI } from './testPrepApi';

const ACCENT = '#1e3a5f';
const ACCENT_LIGHT = '#2a5080';
const WARNING_RED = '#c0392b';
const SUCCESS_GREEN = '#27ae60';
const FLAG_ORANGE = '#e67e22';
const BORDER_GRAY = '#dce1e8';
const BG_LIGHT = '#f4f6f9';
const TEXT_DARK = '#1a1a2e';
const TEXT_MID = '#5a6170';
const WHITE = '#ffffff';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: TEXT_DARK,
    background: BG_LIGHT,
  },

  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 24px',
    background: ACCENT,
    color: WHITE,
    flexShrink: 0,
  },
  sectionName: { fontSize: 16, fontWeight: 600 },
  timer: { fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  timerWarning: { color: '#ff6b6b' },

  mainArea: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
    position: 'relative',
  },
  questionArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '32px 40px',
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
    fontSize: 14,
    color: TEXT_MID,
  },
  progressBarTrack: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    background: BORDER_GRAY,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    borderRadius: 3,
    background: ACCENT,
    transition: 'width 0.3s ease',
  },

  stimulus: {
    fontSize: 16,
    lineHeight: 1.7,
    marginBottom: 28,
    padding: '20px 24px',
    background: WHITE,
    borderRadius: 8,
    border: `1px solid ${BORDER_GRAY}`,
  },

  // RC two-pane layout
  rcLayout: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  passagePane: {
    flex: '0 0 48%',
    overflowY: 'auto',
    padding: '28px 32px',
    borderRight: `1px solid ${BORDER_GRAY}`,
    background: WHITE,
  },
  passagePaneLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: TEXT_MID,
    marginBottom: 14,
  },
  passageText: {
    fontSize: 15,
    lineHeight: 1.85,
    color: TEXT_DARK,
    whiteSpace: 'pre-wrap',
  },
  rcQuestionPane: {
    flex: 1,
    overflowY: 'auto',
    padding: '28px 32px',
    background: BG_LIGHT,
  },
  choicesContainer: { display: 'flex', flexDirection: 'column', gap: 10 },
  choiceButton: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    padding: '14px 18px',
    background: WHITE,
    border: `2px solid ${BORDER_GRAY}`,
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 15,
    lineHeight: 1.5,
    textAlign: 'left',
    transition: 'border-color 0.15s, background 0.15s',
  },
  choiceSelected: {
    borderColor: ACCENT,
    background: '#eaf0f8',
  },
  choiceLetter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: BG_LIGHT,
    fontSize: 13,
    fontWeight: 700,
    color: TEXT_MID,
    flexShrink: 0,
    marginTop: 1,
  },
  choiceLetterSelected: {
    background: ACCENT,
    color: WHITE,
  },

  bottomBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 24px',
    background: WHITE,
    borderTop: `1px solid ${BORDER_GRAY}`,
    flexShrink: 0,
  },
  navGroup: { display: 'flex', gap: 10, alignItems: 'center' },
  btn: {
    padding: '10px 20px',
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    transition: 'opacity 0.15s',
  },
  btnPrimary: { background: ACCENT, color: WHITE },
  btnSecondary: { background: BG_LIGHT, color: TEXT_DARK, border: `1px solid ${BORDER_GRAY}` },
  btnFlag: { background: FLAG_ORANGE, color: WHITE },
  btnSubmit: { background: SUCCESS_GREEN, color: WHITE },
  btnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  navToggle: {
    padding: '10px 16px',
    borderRadius: 6,
    border: `1px solid ${BORDER_GRAY}`,
    background: WHITE,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    color: TEXT_MID,
  },

  navigatorPanel: {
    width: 260,
    background: WHITE,
    borderLeft: `1px solid ${BORDER_GRAY}`,
    padding: '20px 16px',
    overflowY: 'auto',
    flexShrink: 0,
  },
  navPanelTitle: { fontSize: 14, fontWeight: 700, marginBottom: 16, color: TEXT_DARK },
  navGrid: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  navCircle: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: '2px solid transparent',
    transition: 'all 0.15s',
  },
  navDefault: { background: BG_LIGHT, color: TEXT_MID },
  navAnswered: { background: ACCENT, color: WHITE },
  navFlagged: { background: FLAG_ORANGE, color: WHITE },
  navCurrent: { borderColor: TEXT_DARK },

  breakScreen: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: 40,
    textAlign: 'center',
  },
  breakTitle: { fontSize: 28, fontWeight: 700, marginBottom: 16, color: ACCENT },
  breakSubtitle: { fontSize: 16, color: TEXT_MID, marginBottom: 32, lineHeight: 1.6 },

  completeScreen: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: 40,
    textAlign: 'center',
  },
  completeTitle: { fontSize: 32, fontWeight: 700, color: ACCENT, marginBottom: 16 },
  completeSubtitle: { fontSize: 16, color: TEXT_MID, marginBottom: 32 },
};

const CHOICE_LETTERS = ['A', 'B', 'C', 'D', 'E'];

function formatTime(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const TimedTestSessionPlayer = ({
  sceneManifest,
  rendererConfig = {},
  scene,
  feedback,
  children,
}) => {
  const {
    timer_warning_minutes = 35,
    allow_back_within_section = true,
    break_between_sections = 0,
  } = rendererConfig;

  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState(new Set());
  const [timeRemaining, setTimeRemaining] = useState(timer_warning_minutes * 60);
  const [showNavigator, setShowNavigator] = useState(false);
  const [showBreak, setShowBreak] = useState(false);
  const [breakTimeLeft, setBreakTimeLeft] = useState(0);
  const [testComplete, setTestComplete] = useState(false);
  const [sessionId, setSessionId] = useState(scene?.session_id || null);

  const [testMode, setTestMode] = useState(null);
  const [feedbackMode, setFeedbackMode] = useState('at_end');
  const [checkResult, setCheckResult] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [dynamicSections, setDynamicSections] = useState(null);

  const sections = useMemo(
    () => dynamicSections || scene?.sections || sceneManifest?.sections || [],
    [dynamicSections, scene, sceneManifest],
  );

  const timerRef = useRef(null);
  const breakTimerRef = useRef(null);
  const questionStartRef = useRef(Date.now());
  const questionTimesRef = useRef({});

  const section = sections[currentSection] || {};
  const questions = section.questions || [];
  const question = questions[currentQuestion] || {};
  const choices = question.choices || [];
  const answerKey = `${currentSection}-${currentQuestion}`;

  useEffect(() => {
    if (testMode === 'timed' || !testMode) {
      setTimeRemaining(timer_warning_minutes * 60);
    } else {
      setElapsedTime(0);
    }
  }, [currentSection, timer_warning_minutes, testMode]);

  useEffect(() => {
    if (testComplete || showBreak) return;
    if (testMode === 'untimed' || testMode === 'practice') {
      timerRef.current = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleSubmitSection(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [currentSection, testComplete, showBreak, testMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    questionStartRef.current = Date.now();
    setCheckResult(null);
  }, [currentQuestion, currentSection]);

  useEffect(() => {
    if (!showBreak) return;
    setBreakTimeLeft(break_between_sections * 60);
    breakTimerRef.current = setInterval(() => {
      setBreakTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(breakTimerRef.current);
          setShowBreak(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(breakTimerRef.current);
  }, [showBreak, break_between_sections]);

  const handleSelectChoice = useCallback((choiceId) => {
    setAnswers((prev) => ({ ...prev, [answerKey]: choiceId }));
    const timeSpent = (Date.now() - questionStartRef.current) / 1000;
    questionTimesRef.current[answerKey] = timeSpent;

    if (feedbackMode === 'immediate' && sessionId && question.id) {
      testPrepAPI
        .checkAnswer(sessionId, question.id, choiceId)
        .then((res) => setCheckResult(res.data || res))
        .catch(() => {});
    }
  }, [answerKey, feedbackMode, sessionId, question.id]);

  const handleFlag = useCallback(() => {
    setFlagged((prev) => {
      const next = new Set(prev);
      if (next.has(answerKey)) next.delete(answerKey);
      else next.add(answerKey);
      return next;
    });
    if (sessionId && question.id) {
      testPrepAPI.flagQuestion(sessionId, question.id).catch(() => {});
    }
  }, [answerKey, sessionId, question.id]);

  const handleSubmitSection = useCallback((isTimeout = false) => {
    clearInterval(timerRef.current);

    const sectionAnswers = questions.map((q, i) => {
      const key = `${currentSection}-${i}`;
      return {
        question_id: q.id,
        chosen_answer_id: answers[key] || null,
        time_spent_seconds: questionTimesRef.current[key] || 0,
      };
    });

    if (sessionId) {
      testPrepAPI
        .submitSection(sessionId, currentSection, sectionAnswers)
        .catch(() => {});
    }

    if (currentSection < sections.length - 1) {
      if (break_between_sections > 0 && !isTimeout) {
        setShowBreak(true);
      } else {
        setCurrentSection((s) => s + 1);
        setCurrentQuestion(0);
      }
    } else {
      setTestComplete(true);
      if (sessionId) {
        testPrepAPI.scoreTest(sessionId).catch(() => {});
      }
    }
  }, [currentSection, sections.length, questions, answers, sessionId, break_between_sections]);

  const handlePrev = useCallback(() => {
    if (allow_back_within_section && currentQuestion > 0) {
      setCurrentQuestion((q) => q - 1);
    }
  }, [currentQuestion, allow_back_within_section]);

  const handleNext = useCallback(() => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((q) => q + 1);
    }
  }, [currentQuestion, questions.length]);

  const handleNavigatorClick = useCallback((idx) => {
    setCurrentQuestion(idx);
  }, []);

  const handleEndBreak = useCallback(() => {
    clearInterval(breakTimerRef.current);
    setShowBreak(false);
    setCurrentSection((s) => s + 1);
    setCurrentQuestion(0);
  }, []);

  const isWarning = testMode === 'timed' && timeRemaining < 300;
  const answeredCount = questions.reduce(
    (acc, _, i) => acc + (answers[`${currentSection}-${i}`] ? 1 : 0),
    0,
  );

  const handleStartWithMode = useCallback((mode) => {
    const fm = mode === 'practice' ? 'immediate' : 'at_end';
    setTestMode(mode);
    setFeedbackMode(fm);

    if (scene?.test_id) {
      const config = { mode, feedback_mode: fm };
      testPrepAPI
        .startTestSession(scene.test_id, config)
        .then((res) => {
          const data = res.data?.data || res.data;
          if (data?.session_id) setSessionId(data.session_id);
          if (data?.sections) setDynamicSections(data.sections);
        })
        .catch(() => {});
    }
  }, [scene?.test_id]);

  if (!testMode && !sessionId) {
    return (
      <div style={styles.container}>
        <div style={styles.topBar}>
          <span style={styles.sectionName}>Choose Test Mode</span>
        </div>
        <div style={styles.completeScreen}>
          <div style={styles.completeTitle}>How would you like to practice?</div>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center', marginTop: 16 }}>
            {[
              { mode: 'timed', title: 'Timed Test', desc: 'Simulate real test conditions with section time limits' },
              { mode: 'untimed', title: 'Untimed Practice', desc: 'Take your time, review answers at the end' },
              { mode: 'practice', title: 'Check-As-You-Go', desc: 'Get immediate feedback after each question' },
            ].map(({ mode, title, desc }) => (
              <button
                key={mode}
                style={{
                  width: 240,
                  padding: '28px 20px',
                  borderRadius: 12,
                  border: `2px solid ${BORDER_GRAY}`,
                  background: WHITE,
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                }}
                onClick={() => handleStartWithMode(mode)}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = ACCENT; e.currentTarget.style.boxShadow = '0 4px 16px rgba(30,58,95,0.12)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = BORDER_GRAY; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ fontSize: 18, fontWeight: 700, color: ACCENT, marginBottom: 8 }}>{title}</div>
                <div style={{ fontSize: 14, color: TEXT_MID, lineHeight: 1.5 }}>{desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (testComplete) {
    return (
      <div style={styles.container}>
        <div style={styles.topBar}>
          <span style={styles.sectionName}>Test Complete</span>
        </div>
        <div style={styles.completeScreen}>
          <div style={styles.completeTitle}>Test Complete</div>
          <div style={styles.completeSubtitle}>
            All sections have been submitted. Your score report is being prepared.
          </div>
          <button
            style={{ ...styles.btn, ...styles.btnPrimary, padding: '14px 36px', fontSize: 16 }}
            onClick={() => {
              if (scene?.onNavigate) scene.onNavigate('score_report', { sessionId });
            }}
          >
            View Score Report
          </button>
        </div>
      </div>
    );
  }

  if (showBreak) {
    return (
      <div style={styles.container}>
        <div style={styles.topBar}>
          <span style={styles.sectionName}>Break</span>
          <span style={styles.timer}>{formatTime(breakTimeLeft)}</span>
        </div>
        <div style={styles.breakScreen}>
          <div style={styles.breakTitle}>Section Break</div>
          <div style={styles.breakSubtitle}>
            Take a moment to rest before the next section.
            <br />
            The next section will begin automatically, or you can start early.
          </div>
          <button
            style={{ ...styles.btn, ...styles.btnPrimary, padding: '14px 36px', fontSize: 16 }}
            onClick={handleEndBreak}
          >
            Start Next Section
          </button>
        </div>
      </div>
    );
  }

  // RC question: passage text may come as passage_text (from rc_passages join)
  // or as passage (embedded on div_re_* questions)
  const passageText = question.passage_text || question.passage || null;
  const isRCQuestion = !!(passageText);

  const questionPanel = (
    <>
      <div style={styles.progressRow}>
        <span>Question {currentQuestion + 1} of {questions.length}</span>
        <div style={styles.progressBarTrack}>
          <div
            style={{
              ...styles.progressBarFill,
              width: `${((currentQuestion + 1) / Math.max(questions.length, 1)) * 100}%`,
            }}
          />
        </div>
        <span>{answeredCount}/{questions.length} answered</span>
      </div>

      {/* LR stimulus (non-RC questions only) */}
      {question.stimulus && !isRCQuestion && (
        <div style={styles.stimulus}>{question.stimulus}</div>
      )}

      {(question.stem || question.prompt) && (
        <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
          {question.stem || question.prompt}
        </p>
      )}

      <div style={styles.choicesContainer}>
        {choices.map((choice, i) => {
          const cid = choice.id || i;
          const isSelected = answers[answerKey] === cid;
          const isCorrectChoice = checkResult && cid === checkResult.correct_answer_id;
          const isWrongSelected = checkResult && isSelected && !checkResult.is_correct;
          let borderOverride = {};
          if (checkResult) {
            if (isCorrectChoice) borderOverride = { borderColor: SUCCESS_GREEN, background: '#eafaf1' };
            else if (isWrongSelected) borderOverride = { borderColor: WARNING_RED, background: '#fdf2f2' };
          }
          return (
            <button
              key={cid}
              style={{
                ...styles.choiceButton,
                ...(isSelected && !checkResult ? styles.choiceSelected : {}),
                ...borderOverride,
              }}
              onClick={() => !checkResult && handleSelectChoice(cid)}
              disabled={!!checkResult}
            >
              <span
                style={{
                  ...styles.choiceLetter,
                  ...(isSelected && !checkResult ? styles.choiceLetterSelected : {}),
                  ...(isCorrectChoice ? { background: SUCCESS_GREEN, color: WHITE } : {}),
                  ...(isWrongSelected ? { background: WARNING_RED, color: WHITE } : {}),
                }}
              >
                {CHOICE_LETTERS[i] || i + 1}
              </span>
              <span style={{ flex: 1 }}>{choice.text}</span>
            </button>
          );
        })}
      </div>

      {checkResult && (
        <div style={{
          marginTop: 20,
          padding: '16px 20px',
          borderRadius: 8,
          border: `2px solid ${checkResult.is_correct ? SUCCESS_GREEN : WARNING_RED}`,
          background: checkResult.is_correct ? '#eafaf1' : '#fdf2f2',
        }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8, color: checkResult.is_correct ? SUCCESS_GREEN : WARNING_RED }}>
            {checkResult.is_correct ? 'Correct!' : 'Incorrect'}
          </div>
          {typeof checkResult.explanation === 'string' && checkResult.explanation && (
            <div style={{ fontSize: 14, lineHeight: 1.6, color: TEXT_DARK, marginBottom: 10 }}>
              {checkResult.explanation}
            </div>
          )}
          {choices?.length > 0 && (checkResult.choice_explanations || checkResult.choices) && (
            <div style={{ marginTop: 8, borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: TEXT_MID, marginBottom: 6 }}>
                Answer breakdown
              </div>
              {choices.map((choice, i) => {
                const cid = choice.id || i;
                const isChoiceCorrect = cid === checkResult.correct_answer_id;
                const fromResult = (checkResult.choices || []).find((c) => (c.id || c.choice_id) === cid);
                const expl = (
                  fromResult?.explanation
                  || checkResult.choice_explanations?.[cid]
                  || choice.explanation
                  || ''
                );
                return (
                  <div key={cid} style={{
                    display: 'flex', gap: 6, marginBottom: 5, fontSize: 13, lineHeight: 1.5,
                    color: isChoiceCorrect ? SUCCESS_GREEN : TEXT_DARK,
                  }}>
                    <span style={{ fontWeight: 700, minWidth: 22 }}>
                      {isChoiceCorrect ? '✓' : '✗'} {CHOICE_LETTERS[i] || i + 1}.
                    </span>
                    <span>
                      <strong>{choice.text}</strong>
                      {expl ? ` — ${expl}` : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {checkResult.reasoning_prompt && (
            <div style={{
              marginTop: 10, padding: '8px 12px', background: '#eef2ff',
              borderRadius: 4, fontSize: 12, color: '#3730a3',
            }}>
              <strong>Key insight:</strong> {checkResult.reasoning_prompt}
            </div>
          )}
          {checkResult.concepts_tested?.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 13, color: TEXT_MID }}>
              Concepts: {checkResult.concepts_tested.join(', ')}
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <div style={styles.container}>
      <div style={styles.topBar}>
        <span style={styles.sectionName}>
          {section.name || `Section ${currentSection + 1}`}
        </span>
        <span style={{ ...styles.timer, ...(testMode === 'timed' && isWarning ? styles.timerWarning : {}) }}>
          {testMode === 'untimed' || testMode === 'practice'
            ? formatTime(elapsedTime)
            : formatTime(timeRemaining)}
        </span>
      </div>

      <div style={styles.mainArea}>
        {isRCQuestion ? (
          /* ── Reading Comprehension: two-pane layout ── */
          <div style={styles.rcLayout}>
            <div style={styles.passagePane}>
              <div style={styles.passagePaneLabel}>Reading Passage</div>
              <div style={styles.passageText}>{passageText}</div>
            </div>
            <div style={styles.rcQuestionPane}>
              {questionPanel}
            </div>
          </div>
        ) : (
          /* ── Logical Reasoning / other: single-column ── */
          <div style={styles.questionArea}>
            {questionPanel}
          </div>
        )}

        {showNavigator && (
          <div style={styles.navigatorPanel}>
            <div style={styles.navPanelTitle}>Question Navigator</div>
            <div style={styles.navGrid}>
              {questions.map((_, i) => {
                const key = `${currentSection}-${i}`;
                const isAnswered = !!answers[key];
                const isFlagged = flagged.has(key);
                const isCurrent = i === currentQuestion;
                return (
                  <div
                    key={i}
                    style={{
                      ...styles.navCircle,
                      ...(isFlagged
                        ? styles.navFlagged
                        : isAnswered
                          ? styles.navAnswered
                          : styles.navDefault),
                      ...(isCurrent ? styles.navCurrent : {}),
                    }}
                    onClick={() => handleNavigatorClick(i)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Question ${i + 1}${isAnswered ? ', answered' : ''}${isFlagged ? ', flagged' : ''}`}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleNavigatorClick(i); }}
                  >
                    {i + 1}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div style={styles.bottomBar}>
        <div style={styles.navGroup}>
          <button
            style={{
              ...styles.btn,
              ...styles.btnSecondary,
              ...(!allow_back_within_section || currentQuestion === 0
                ? styles.btnDisabled
                : {}),
            }}
            onClick={handlePrev}
            disabled={!allow_back_within_section || currentQuestion === 0}
          >
            ← Prev
          </button>
          {currentQuestion < questions.length - 1 ? (
            <button
              style={{ ...styles.btn, ...styles.btnPrimary }}
              onClick={handleNext}
            >
              Next →
            </button>
          ) : (
            <button
              style={{ ...styles.btn, ...styles.btnSubmit }}
              onClick={() => handleSubmitSection(false)}
            >
              Submit Section
            </button>
          )}
          <button
            style={{
              ...styles.btn,
              ...(flagged.has(answerKey)
                ? styles.btnFlag
                : styles.btnSecondary),
            }}
            onClick={handleFlag}
          >
            {flagged.has(answerKey) ? '⚑ Flagged' : '⚐ Flag'}
          </button>
        </div>
        <button
          style={styles.navToggle}
          onClick={() => setShowNavigator((v) => !v)}
        >
          {showNavigator ? 'Hide Navigator' : 'Show Navigator'}
        </button>
      </div>
    </div>
  );
};

export default TimedTestSessionPlayer;
